/* global __APP_VERSION__, __COMMIT_SHA__ */

const API_BASE = import.meta.env.VITE_API_URL;
const recentReports = new Map();
const recentActions = [];
const DEDUPE_WINDOW_MS = 30_000;
const MAX_RECENT_ACTIONS = 10;
let lastUserAction = null;
let lastUserActionAt = 0;
let lastApiFailure = null;
let lastApiFailureAt = 0;

const APP_VERSION = __APP_VERSION__;
const COMMIT_SHA = __COMMIT_SHA__;

const normalizeError = (error) => {
  if (error instanceof Error) {
    return {
      message: error.message || error.name,
      name: error.name || "Error",
      stack: error.stack || null,
    };
  }
  if (typeof error === "string") {
    return { message: error, name: "Error", stack: null };
  }
  try {
    return { message: JSON.stringify(error), name: "Error", stack: null };
  } catch {
    return { message: "Unknown browser error", name: "Error", stack: null };
  }
};

const readJsonStorage = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
};

const getSafeIdentity = () => {
  const user = readJsonStorage("ai_escape_game_user");
  const session = readJsonStorage("ai_escape_game_session");
  const userId = user?.id || user?.user_id || user?.username || null;
  const sessionId = session?.session_id || session?.id || null;
  return {
    userId: userId === null ? null : String(userId),
    sessionId: sessionId === null ? null : String(sessionId),
  };
};

export const recordRecentAction = (action, { userAction = false } = {}) => {
  if (!action) return;
  const safeAction = String(action).replace(/\s+/g, " ").trim().slice(0, 500);
  if (!safeAction || recentActions.at(-1) === safeAction) return;
  recentActions.push(safeAction);
  if (recentActions.length > MAX_RECENT_ACTIONS) recentActions.shift();
  if (userAction) {
    lastUserAction = safeAction;
    lastUserActionAt = Date.now();
  }
};

export const recordApiFailure = (api) => {
  lastApiFailureAt = Date.now();
  lastApiFailure = {
    method: api.method || "GET",
    path: api.path || "Unknown",
    status: api.status ?? null,
    duration_ms: api.durationMs ?? null,
    request_id: api.requestId || null,
    response: api.response ? String(api.response).slice(0, 2_000) : null,
  };
  recordRecentAction(
    `${lastApiFailure.method} ${lastApiFailure.path} returned ${lastApiFailure.status || "a network error"}`,
  );
  return lastApiFailure;
};

export const reportClientError = (error, context = {}) => {
  // Reporting is deliberately fire-and-forget and never rejects into app code.
  if (!API_BASE) return;

  try {
    const normalized = normalizeError(error);
    const fingerprint = `${context.source || "browser"}:${normalized.message}:${window.location.pathname}`;
    const lastReportedAt = recentReports.get(fingerprint) || 0;
    if (Date.now() - lastReportedAt < DEDUPE_WINDOW_MS) return;
    recentReports.set(fingerprint, Date.now());
    const identity = getSafeIdentity();

    const payload = {
      message: normalized.message.slice(0, 2_000),
      error_type: (context.errorType || normalized.name).slice(0, 120),
      source: (context.source || "browser").slice(0, 80),
      page_url: `${window.location.origin}${window.location.pathname}`,
      app_version: APP_VERSION,
      commit: COMMIT_SHA,
      user_action: context.userAction || lastUserAction,
      user_id: context.userId || identity.userId,
      session_id: context.sessionId || identity.sessionId,
      browser_stack: (context.browserStack || normalized.stack || "").slice(0, 20_000) || null,
      component_stack: (context.componentStack || "").slice(0, 20_000) || null,
      api:
        context.api ||
        (Date.now() - lastApiFailureAt <= 60_000 ? lastApiFailure : null),
      recent_actions: recentActions.slice(-MAX_RECENT_ACTIONS),
      metadata: context.metadata || {},
    };

    void fetch(`${API_BASE}/errors/client`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Error reporting must never become another application error.
  }
};

const describeControl = (element) => {
  const text = element.getAttribute("aria-label") || element.textContent || "control";
  return text.replace(/\s+/g, " ").trim().slice(0, 100);
};

const describeField = (element) => {
  const name = element.getAttribute("aria-label") || element.name || element.type || "field";
  return String(name).replace(/[-_]+/g, " ").trim();
};

export const installGlobalErrorReporting = () => {
  recordRecentAction(`Opened ${window.location.pathname}`);

  for (const method of ["pushState", "replaceState"]) {
    const original = window.history[method].bind(window.history);
    window.history[method] = (...args) => {
      const result = original(...args);
      recordRecentAction(`Opened ${window.location.pathname}`);
      return result;
    };
  }

  document.addEventListener(
    "click",
    (event) => {
      const control = event.target instanceof Element
        ? event.target.closest("button, a, [role='button']")
        : null;
      if (control) {
        recordRecentAction(`Clicked "${describeControl(control)}"`, { userAction: true });
      }
    },
    true,
  );

  document.addEventListener(
    "change",
    (event) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        recordRecentAction(`Entered ${describeField(event.target)}`, { userAction: true });
      }
    },
    true,
  );

  document.addEventListener(
    "submit",
    (event) => {
      if (Date.now() - lastUserActionAt < 500) return;
      const button = event.target instanceof HTMLFormElement
        ? event.target.querySelector("button[type='submit']")
        : null;
      recordRecentAction(
        button ? `Submitted "${describeControl(button)}"` : "Submitted form",
        { userAction: true },
      );
    },
    true,
  );

  window.addEventListener("popstate", () => {
    recordRecentAction(`Opened ${window.location.pathname}`);
  });

  window.addEventListener("error", (event) => {
    reportClientError(event.error || event.message, {
      source: "window.error",
      metadata: {
        filename: event.filename || null,
        line: event.lineno || null,
        column: event.colno || null,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportClientError(event.reason, { source: "unhandledrejection" });
  });
};
